/**
 * Log sinks — destinations for LogRecords.
 *
 * Each sink implements LogSink and handles one delivery mechanism.
 * New destinations = new sink class (OCP).
 */

import type { LogLevel, LogRecord, LogSink } from "./logger";

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// ---------------------------------------------------------------------------
// ConsoleSink — mirrors records into the browser devtools console.
// ---------------------------------------------------------------------------

export class ConsoleSink implements LogSink {
  constructor(private readonly minLevel: LogLevel = "info") {}

  write(record: LogRecord): void {
    if (LEVEL_ORDER[record.level] < LEVEL_ORDER[this.minLevel]) return;
    const fn =
      record.level === "error"
        ? console.error
        : record.level === "warn"
          ? console.warn
          : record.level === "debug"
            ? console.debug
            : console.log;
    const ctx = record.context && Object.keys(record.context).length ? ` ${JSON.stringify(record.context)}` : "";
    fn(`[${record.logger}] ${record.message}${ctx}`, record.extra ?? "");
  }
}

// ---------------------------------------------------------------------------
// MemorySink — in-memory ring buffer for the DebugConsole component.
// ---------------------------------------------------------------------------

export class MemorySink implements LogSink {
  private readonly _entries: LogRecord[] = [];
  private readonly _listeners: Set<() => void> = new Set();

  constructor(private readonly maxEntries: number = 200) {}

  write(record: LogRecord): void {
    this._entries.push(record);
    if (this._entries.length > this.maxEntries) this._entries.shift();
    for (const fn of this._listeners) {
      fn();
    }
  }

  entries(): readonly LogRecord[] {
    return this._entries;
  }

  clear(): void {
    this._entries.length = 0;
    for (const fn of this._listeners) {
      fn();
    }
  }

  subscribe(listener: () => void): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }
}

// ---------------------------------------------------------------------------
// ServerSink — fire-and-forget POST to /api/debug-log so backend sinks
// (StdoutSink + FileSink) receive frontend records alongside backend ones.
// ---------------------------------------------------------------------------

export class ServerSink implements LogSink {
  constructor(
    private readonly endpoint: string = "/api/debug-log",
    private readonly minLevel: LogLevel = "info",
  ) {}

  write(record: LogRecord): void {
    if (LEVEL_ORDER[record.level] < LEVEL_ORDER[this.minLevel]) return;
    const body = {
      level: record.level,
      logger: record.logger,
      event: record.message,
      timestamp: record.timestamp,
      context: record.context,
      ...(record.extra ?? {}),
    };
    try {
      fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // swallow — sinks must not throw
    }
  }
}
